import React, { useEffect, useState } from 'react';
import { useTranslations, useLocale } from 'use-intl';
import { ChevronUp, ChevronDown, Plus, Edit2, Trash2, X, Save, Info } from 'react-feather';
import { useOfferingVehiclesStore } from '@/pages/SupportClients/useOfferingVehicles';
import { OfferingVehicle, FuelType, VehicleCategory } from '@/pages/CustomerSupport/types';
import Button from '@/stories/Library/Components/Button/Button';
import InputField from '@/stories/Library/Components/InputField/InputField';
import MoneyInputField from '@/stories/Library/Components/MoneyInputField/MoneyInputField';
import TextArea from '@/stories/Library/Components/TextArea/TextArea';
import Modal from '@/stories/Library/Components/Modal/Modal';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { getLanguageFromPath } from '@/utils/utils';
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell
} from '@/stories/Shadcn/Table/Table';

interface OfferingVehiclesSectionProps {
	organizationId: string | null;
	embeddedInTab?: boolean;
}

type VehicleFormData = {
	name: string;
	minimum_weekly_payment: number;
	recommendation_priority: number;
	fuel_type: FuelType;
	category: VehicleCategory;
	description: string;
	is_active: boolean;
};

const defaultFormData: VehicleFormData = {
	name: '',
	minimum_weekly_payment: 0,
	recommendation_priority: 0,
	fuel_type: 'fuel',
	category: 'low_end',
	description: '',
	is_active: true
};

const OfferingVehiclesSection: React.FC<OfferingVehiclesSectionProps> = ({
	organizationId,
	embeddedInTab = false
}) => {
	const t = useTranslations('SupportClients');
	const locale = useLocale();
	const language = getLanguageFromPath(window.location.pathname) || locale;

	const { vehicles, isLoading, error, loadVehicles, insertVehicle, updateVehicle, deleteVehicle } =
		useOfferingVehiclesStore();

	const [isExpanded, setIsExpanded] = useState(true);
	const [isFormModalOpen, setIsFormModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [editingVehicle, setEditingVehicle] = useState<OfferingVehicle | null>(null);
	const [deletingVehicle, setDeletingVehicle] = useState<OfferingVehicle | null>(null);
	const [formData, setFormData] = useState<VehicleFormData>(defaultFormData);

	useEffect(() => {
		if (organizationId) {
			loadVehicles(organizationId);
		}
	}, [organizationId, loadVehicles]);

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat('es-MX', {
			style: 'currency',
			currency: 'MXN',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(value);
	};

	const formatDate = (dateString: string | null) => {
		if (!dateString) return 'N/A';
		const localeCode = language === 'es' ? 'es-MX' : 'en-US';
		return new Date(dateString).toLocaleDateString(localeCode, {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	};

	const getFuelTypeLabel = (fuelType: FuelType) => {
		const labels: Record<FuelType, string> = {
			fuel: t('VehicleTypeFuel'),
			electric: t('VehicleTypeElectric')
		};
		return labels[fuelType] || fuelType;
	};

	const getCategoryLabel = (category: VehicleCategory) => {
		const labels: Record<VehicleCategory, string> = {
			low_end: t('CategoryLowEnd'),
			high_end: t('CategoryHighEnd')
		};
		return labels[category] || category;
	};

	const handleOpenCreateModal = () => {
		setEditingVehicle(null);
		setFormData(defaultFormData);
		setIsFormModalOpen(true);
	};

	const handleOpenEditModal = (vehicle: OfferingVehicle) => {
		setEditingVehicle(vehicle);
		setFormData({
			name: vehicle.name,
			minimum_weekly_payment: vehicle.minimum_weekly_payment,
			recommendation_priority: vehicle.recommendation_priority ?? 0,
			fuel_type: vehicle.fuel_type,
			category: vehicle.category,
			description: vehicle.description || '',
			is_active: vehicle.is_active
		});
		setIsFormModalOpen(true);
	};

	const handleOpenDeleteModal = (vehicle: OfferingVehicle) => {
		setDeletingVehicle(vehicle);
		setIsDeleteModalOpen(true);
	};

	const handleCloseFormModal = () => {
		setIsFormModalOpen(false);
		setEditingVehicle(null);
		setFormData(defaultFormData);
	};

	const handleCloseDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setDeletingVehicle(null);
	};

	const handleSave = async () => {
		if (!organizationId || !formData.name) return;

		const vehicleData = {
			name: formData.name,
			minimum_weekly_payment: formData.minimum_weekly_payment,
			recommendation_priority: formData.recommendation_priority,
			fuel_type: formData.fuel_type,
			category: formData.category,
			description: formData.description || null,
			is_active: formData.is_active
		};

		let success: boolean;
		if (editingVehicle) {
			success = await updateVehicle(editingVehicle.id, organizationId, vehicleData);
		} else {
			success = await insertVehicle(organizationId, vehicleData);
		}

		if (success) {
			handleCloseFormModal();
		}
	};

	const handleDelete = async () => {
		if (!organizationId || !deletingVehicle) return;

		const success = await deleteVehicle(deletingVehicle.id, organizationId);
		if (success) {
			handleCloseDeleteModal();
		}
	};

	const parseMoneyValue = (value: string | null): number => {
		if (!value) return 0;
		const cleaned = value.replace(/[^\d.]/g, '');
		const parsed = parseFloat(cleaned);
		return isNaN(parsed) ? 0 : parsed;
	};

	const renderContent = () => (
		<div className="mt-4">
			<div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg flex gap-3">
				<Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
				<div className="text-sm text-gray-700">
					<p className="font-medium text-gray-900 mb-1">{t('VehicleRecommendationHowItWorks')}</p>
					<p>{t('VehicleRecommendationCalculationExplanation')}</p>
				</div>
			</div>
			{isLoading ? (
				<p className="text-gray-500 text-center py-6">{t('LoadingVehicles')}</p>
			) : vehicles.length === 0 ? (
				<p className="text-gray-500 text-center py-6">{t('NoVehicles')}</p>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{t('VehicleName')}</TableHead>
							<TableHead>{t('RecommendationPriority')}</TableHead>
							<TableHead>{t('MinimumPayment')}</TableHead>
							<TableHead>{t('FuelType')}</TableHead>
							<TableHead>{t('Category')}</TableHead>
							<TableHead>{t('Status')}</TableHead>
							<TableHead>{t('CreatedAt')}</TableHead>
							<TableHead>{t('Actions')}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{vehicles.map((vehicle) => (
							<TableRow key={vehicle.id}>
								<TableCell className="font-medium">{vehicle.name}</TableCell>
								<TableCell>{vehicle.recommendation_priority ?? 0}</TableCell>
								<TableCell>{formatCurrency(vehicle.minimum_weekly_payment)}</TableCell>
								<TableCell>{getFuelTypeLabel(vehicle.fuel_type)}</TableCell>
								<TableCell>{getCategoryLabel(vehicle.category)}</TableCell>
								<TableCell>
									<span
										className={`px-2 py-1 rounded-full text-xs font-medium ${
											vehicle.is_active
												? 'bg-green-100 text-green-800'
												: 'bg-gray-100 text-gray-800'
										}`}
									>
										{vehicle.is_active ? t('Active') : t('Inactive')}
									</span>
								</TableCell>
								<TableCell className="text-gray-500">{formatDate(vehicle.created_at)}</TableCell>
								<TableCell>
									<div className="flex items-center gap-2">
										<button
											onClick={() => handleOpenEditModal(vehicle)}
											className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900"
										>
											<Edit2 size={16} />
										</button>
										<button
											onClick={() => handleOpenDeleteModal(vehicle)}
											className="p-1 hover:bg-red-50 rounded text-gray-600 hover:text-red-600"
										>
											<Trash2 size={16} />
										</button>
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</div>
	);

	return (
		<div
			className={`bg-white rounded-lg border border-gray-200 p-6 ${embeddedInTab ? '' : 'mb-6'}`}
		>
			{embeddedInTab ? (
				<>
					<div className="flex justify-end mb-4">
						<Button
							label={t('AddVehicle')}
							onClick={handleOpenCreateModal}
							startIcon={<Plus size={16} />}
							color="primary"
							size="small"
						/>
					</div>
					{renderContent()}
				</>
			) : (
				<>
					<div
						className="flex items-center justify-between cursor-pointer"
						onClick={() => setIsExpanded(!isExpanded)}
					>
						<h2 className="text-lg font-semibold">{t('OfferingVehicles')}</h2>
						<div className="flex items-center gap-2">
							{isExpanded && (
								<div onClick={(e) => e.stopPropagation()}>
									<Button
										label={t('AddVehicle')}
										onClick={handleOpenCreateModal}
										startIcon={<Plus size={16} />}
										color="primary"
										size="small"
									/>
								</div>
							)}
							{isExpanded ? (
								<ChevronUp size={20} className="text-gray-500" />
							) : (
								<ChevronDown size={20} className="text-gray-500" />
							)}
						</div>
					</div>
					{isExpanded && renderContent()}
				</>
			)}

			<Modal isOpen={isFormModalOpen} closeable onClose={handleCloseFormModal} maxWidth={600}>
				<div className="flex flex-col gap-6">
					<Typography type="display" size="sm">
						{editingVehicle ? t('EditVehicle') : t('CreateVehicle')}
					</Typography>

					<InputField
						label={t('VehicleName')}
						type="text"
						value={formData.name}
						onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
						size="default"
					/>

					<InputField
						label={t('RecommendationPriority')}
						type="number"
						value={formData.recommendation_priority.toString()}
						onChange={(e) =>
							setFormData((prev) => ({
								...prev,
								recommendation_priority: parseInt(e.target.value, 10) || 0
							}))
						}
						size="default"
					/>

					<MoneyInputField
						label={t('MinimumPayment')}
						value={formData.minimum_weekly_payment.toString()}
						onChange={(value) =>
							setFormData((prev) => ({ ...prev, minimum_weekly_payment: parseMoneyValue(value) }))
						}
						size="default"
					/>

					<div className="flex flex-col gap-2">
						<label className="text-sm font-medium text-gray-700">{t('FuelType')}</label>
						<select
							value={formData.fuel_type}
							onChange={(e) =>
								setFormData((prev) => ({ ...prev, fuel_type: e.target.value as FuelType }))
							}
							className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
						>
							<option value="fuel">{t('VehicleTypeFuel')}</option>
							<option value="electric">{t('VehicleTypeElectric')}</option>
						</select>
					</div>

					<div className="flex flex-col gap-2">
						<label className="text-sm font-medium text-gray-700">{t('Category')}</label>
						<select
							value={formData.category}
							onChange={(e) =>
								setFormData((prev) => ({ ...prev, category: e.target.value as VehicleCategory }))
							}
							className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
						>
							<option value="low_end">{t('CategoryLowEnd')}</option>
							<option value="high_end">{t('CategoryHighEnd')}</option>
						</select>
					</div>

					<TextArea
						label={t('Description')}
						value={formData.description}
						onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
						rows={3}
					/>

					<div className="flex items-center gap-2">
						<input
							type="checkbox"
							id="is_active"
							checked={formData.is_active}
							onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
							className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
						/>
						<label htmlFor="is_active" className="text-sm font-medium text-gray-700">
							{t('IsActive')}
						</label>
					</div>

					{error && <p className="text-red-500 text-sm">{error}</p>}

					<div className="flex gap-2 pt-2">
						<Button
							label={t('Cancel')}
							onClick={handleCloseFormModal}
							startIcon={<X size={16} />}
							color="secondary"
							size="medium"
							outlined
						/>
						<Button
							label={t('Save')}
							onClick={handleSave}
							startIcon={<Save size={16} />}
							color="primary"
							size="medium"
						/>
					</div>
				</div>
			</Modal>

			<Modal isOpen={isDeleteModalOpen} closeable onClose={handleCloseDeleteModal} maxWidth={400}>
				<div className="flex flex-col gap-6">
					<Typography type="display" size="sm">
						{t('DeleteVehicle')}
					</Typography>

					<p className="text-gray-600">
						{t('DeleteVehicleConfirmation', { name: deletingVehicle?.name || '' })}
					</p>

					{error && <p className="text-red-500 text-sm">{error}</p>}

					<div className="flex gap-2 pt-2">
						<Button
							label={t('Cancel')}
							onClick={handleCloseDeleteModal}
							color="secondary"
							size="medium"
							outlined
						/>
						<Button
							label={t('Delete')}
							onClick={handleDelete}
							startIcon={<Trash2 size={16} />}
							color="primary"
							size="medium"
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default OfferingVehiclesSection;
